import { configureToast } from '@atrvd/saas-ui'

/**
 * How long an error toast stays: until the user closes it (DCP-365 AC.1).
 *
 * `Infinity` is sonner's documented "no auto-dismiss" value, not a large
 * number — it returns before arming the dismiss timer at all. The close button
 * is what makes that safe rather than a trap: an un-dismissable toast otherwise
 * responds only to a swipe, which nothing on screen advertises (AC.2).
 */
const ERROR_TOAST_DEFAULTS = {
  duration: Number.POSITIVE_INFINITY,
  closeButton: true,
} as const

/**
 * Apply the app's toast policy. Call once at startup, before rendering.
 *
 * A success toast confirms something the user just did — they already know, so
 * it can fade. An error toast carries information the user does NOT have yet,
 * and often the only copy of it: a rejected save, a failed import, the row
 * number a spreadsheet choked on. Sonner's 4-second default is long enough to
 * notice a confirmation and too short to read a diagnosis.
 *
 * One call, not 189 edits: every `toast.error` in the app imports `toast`
 * straight from saas-ui, so the rule belongs between the call sites and sonner
 * rather than at each of them. Sonner has no per-kind default of its own —
 * `<Toaster duration>` is a single number shared by every kind, so raising it
 * there would pin success and info toasts on screen too — which is why
 * `configureToast` was added to saas-ui (izdatcom/saas-ui#21).
 *
 * Call sites keep the last word: `toast.error(msg, { duration: 4000 })` still
 * self-clears where that is genuinely wanted.
 */
export function configureAppToasts(): void {
  configureToast({ error: ERROR_TOAST_DEFAULTS })
}
